/** @file partPointOf: the host's part transform of one entity-relative world point (CR-006, 22.3 b). */
import type { Vec3 } from '@zilo/pose3d-math/vec3/vec3';
import type { PoseParams, ShakeWave } from '@zilo/pose3d-pose/contract';
import { SHAKE_WAVE_DEFAULT } from '@zilo/pose3d-pose/chain/shakeWaveDefault';

/** One node's host part transform, `PoseParams.partTransform[node]`. */
export type PartTransform = NonNullable<PoseParams['partTransform']>[string];

/**
 * Design 22.3 (b), on a point after mirror and facing and before rounding: with `z0 = z`,
 * `x' = x + skew[0] * z0 + shift[0]`, `y' = y + skew[1] * z0 + shift[1]`, `z' = z + shift[2]`; then, when `shakePx` is
 * not 0, `x' += (shakePx / ppu) * sin(pi * (t * rate + (z' + y') * spatial))` with `t = floor(shakeClock * stepHz) / stepHz`
 * (`wave`, default `SHAKE_WAVE_DEFAULT`). `ppu` = pixels per unit across. No record (an exempt node) returns `point` itself.
 */
export function partPointOf(point: Vec3, part: PartTransform | undefined, shakeClock: number, ppu: number,
  wave: ShakeWave = SHAKE_WAVE_DEFAULT): Vec3 {
  if (part === undefined) return point;
  const [x, y, z] = point;
  const shift = part.shift ?? [0, 0, 0];
  const skew = part.skew ?? [0, 0];
  let x1 = x + skew[0] * z + shift[0];
  const y1 = y + skew[1] * z + shift[1];
  const z1 = z + shift[2];
  const shake = part.shakePx ?? 0;
  if (shake !== 0) {
    const t = Math.floor(shakeClock * wave.stepHz) / wave.stepHz;
    x1 += (shake / ppu) * Math.sin(Math.PI * (t * wave.rate + (z1 + y1) * wave.spatial));
  }
  return [x1, y1, z1];
}
